import { useState, useEffect } from 'react';
import { supabase } from '../shared/supabase';

const PremiumContentManagement = () => {
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'video',
    category: 'manifestation',
    price: 4.99,
    is_free: false,
    duration: '',
    thumbnail_url: '',
    content_url: '',
    order_index: 0
  });

  useEffect(() => {
    loadContents();
  }, []);

  const loadContents = async () => {
    try {
      const { data, error } = await supabase
        .from('premium_content')
        .select('*')
        .order('order_index', { ascending: true });

      if (error) throw error;
      setContents(data || []);
    } catch (error) {
      console.error('Error loading content:', error);
      alert('Error loading content');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file, type) => {
    if (!file) return null;

    setUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `${type}/${fileName}`;

      const { data, error } = await supabase.storage
        .from('premium-content')
        .upload(filePath, file);

      if (error) throw error;

      const { data: urlData } = supabase.storage
        .from('premium-content')
        .getPublicUrl(filePath);

      return urlData.publicUrl;
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Error uploading file: ' + error.message);
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const url = await handleFileUpload(file, 'thumbnails');
    if (url) {
      setFormData({ ...formData, thumbnail_url: url });
    }
  };

  const handleContentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const folder = formData.type === 'video' ? 'videos' : 
                   formData.type === 'audio' ? 'audio' : 'documents';
    
    const url = await handleFileUpload(file, folder);
    if (url) {
      setFormData({ ...formData, content_url: url });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.content_url) {
      alert('Please fill in all required fields and upload content');
      return;
    }

    try {
      if (editingContent) {
        const { error } = await supabase
          .from('premium_content')
          .update(formData)
          .eq('id', editingContent.id);

        if (error) throw error;
        alert('Content updated successfully!');
      } else {
        const { error } = await supabase
          .from('premium_content')
          .insert([formData]);

        if (error) throw error;
        alert('Content created successfully!');
      }

      setShowForm(false);
      setEditingContent(null);
      resetForm();
      loadContents();
    } catch (error) {
      console.error('Error saving content:', error);
      alert('Error saving content: ' + error.message);
    }
  };

  const handleEdit = (content) => {
    setEditingContent(content);
    setFormData(content);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this content?')) return;

    try {
      const { error } = await supabase
        .from('premium_content')
        .delete()
        .eq('id', id);

      if (error) throw error;
      alert('Content deleted successfully!');
      loadContents();
    } catch (error) {
      console.error('Error deleting content:', error);
      alert('Error deleting content');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      type: 'video',
      category: 'manifestation',
      price: 4.99,
      is_free: false,
      duration: '',
      thumbnail_url: '',
      content_url: '',
      order_index: 0
    });
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingContent(null);
    resetForm();
  };

  if (loading) {
    return <div className="admin-loading">Loading...</div>;
  }

  return (
    <div className="admin-content">
      <div className="admin-header">
        <div>
          <h1>Premium Content Management</h1>
          <p>Manage videos, audio files, and PDF guides</p>
        </div>
        <button 
          className="btn-primary"
          onClick={() => setShowForm(true)}
        >
          + Add New Content
        </button>
      </div>

      {showForm && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '600px' }}>
            <h2>{editingContent ? 'Edit Content' : 'Add New Content'}</h2>
            <form onSubmit={handleSubmit} className="content-form">
              <div className="form-row">
                <label>
                  Title *
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                  />
                </label>
              </div>

              <div className="form-row">
                <label>
                  Description
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows="3"
                  />
                </label>
              </div>

              <div className="form-row-split">
                <label>
                  Type *
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  >
                    <option value="video">Video</option>
                    <option value="audio">Audio</option>
                    <option value="pdf">PDF</option>
                  </select>
                </label>

                <label>
                  Category
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    <option value="Confidence">💪 Confidence</option>
                    <option value="Love">❤️ Love</option>
                    <option value="Abundance">✨ Abundance</option>
                    <option value="Healing">🌙 Healing</option>
                    <option value="Gratitude">🙏 Gratitude</option>
                    <option value="Focus">🎯 Focus</option>
                    <option value="Clarity">💎 Clarity</option>
                    <option value="Joy">☀️ Joy</option>
                    <option value="Courage">🦁 Courage</option>
                  </select>
                </label>
              </div>

              <div className="form-row-split">
                <label>
                  Price ($)
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                    disabled={formData.is_free}
                  />
                </label>

                <label>
                  Duration
                  <input
                    type="text"
                    placeholder="e.g. 15:30"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  />
                </label>
              </div>

              <div className="form-row">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_free}
                    onChange={(e) => setFormData({ ...formData, is_free: e.target.checked })}
                  />
                  <span>Free Content (No payment required)</span>
                </label>
              </div>

              <div className="form-row">
                <label>
                  Thumbnail Image
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleThumbnailUpload}
                    disabled={uploading}
                  />
                  {formData.thumbnail_url && (
                    <img 
                      src={formData.thumbnail_url} 
                      alt="Preview" 
                      style={{ width: '100px', marginTop: '10px', borderRadius: '8px' }}
                    />
                  )}
                </label>
              </div>

              <div className="form-row">
                <label>
                  Content File * ({formData.type === 'video' ? 'Video' : formData.type === 'audio' ? 'Audio' : 'PDF'})
                  <input
                    type="file"
                    accept={
                      formData.type === 'video' ? 'video/*' :
                      formData.type === 'audio' ? 'audio/*' :
                      'application/pdf'
                    }
                    onChange={handleContentUpload}
                    disabled={uploading}
                  />
                  {formData.content_url && (
                    <div style={{ marginTop: '10px', color: 'green' }}>
                      ✓ File uploaded successfully
                    </div>
                  )}
                </label>
              </div>

              {uploading && (
                <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                  Uploading file... Please wait.
                </div>
              )}

              <div className="form-actions">
                <button type="button" onClick={handleCancel} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={uploading}>
                  {editingContent ? 'Update' : 'Create'} Content
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="content-grid">
        {contents.length === 0 ? (
          <div className="empty-state">
            <p>No premium content yet. Click "Add New Content" to get started.</p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Thumbnail</th>
                <th>Title</th>
                <th>Type</th>
                <th>Category</th>
                <th>Price</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {contents.map((content) => (
                <tr key={content.id}>
                  <td>
                    {content.thumbnail_url ? (
                      <img 
                        src={content.thumbnail_url} 
                        alt={content.title}
                        style={{ width: '60px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
                      />
                    ) : (
                      <div style={{ width: '60px', height: '40px', background: '#ddd', borderRadius: '4px' }} />
                    )}
                  </td>
                  <td>
                    <strong>{content.title}</strong>
                    {content.duration && <div style={{ fontSize: '12px', color: '#666' }}>{content.duration}</div>}
                  </td>
                  <td>
                    <span className="badge">{content.type}</span>
                  </td>
                  <td>{content.category}</td>
                  <td>
                    {content.is_free ? (
                      <span style={{ color: 'green', fontWeight: 'bold' }}>FREE</span>
                    ) : (
                      `$${content.price}`
                    )}
                  </td>
                  <td>
                    <span className={`status-badge ${content.is_free ? 'free' : 'premium'}`}>
                      {content.is_free ? 'Free' : 'Premium'}
                    </span>
                  </td>
                  <td>
                    <button onClick={() => handleEdit(content)} className="btn-icon">✏️</button>
                    <button onClick={() => handleDelete(content.id)} className="btn-icon">🗑️</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default PremiumContentManagement;